import React, { type ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { chainflipCommunityLinks } from '@chainflip/utils/consts';
import { abbreviate } from '@chainflip/utils/string';
import classNames from 'classnames';
import { ChainflipLogo } from '@/shared/assets/chainflip-logo';
import { Button, Link } from '@/shared/components';
import { ChainflipNetworkBadge } from '@/shared/components/atoms/ChainflipNetworkBadge';
import { type LinkProps } from '@/shared/components/atoms/Link';
import { ConnectAvatar } from '@/shared/components/RainbowKit';
import { isStablecoinStrategiesEnabled } from '@/shared/featureFlags';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import {
  CloseIcon,
  HamburgerIcon,
  DiscordIcon,
  TelegramIcon,
  TwitterIcon,
  PowerIcon,
} from '@/shared/icons/large';
import { MiscNavDropdownLinks } from './MiscNavDropdownLinks';
import { PolkaConnectInfo } from './PolkaConnectInfo';

type ExtendedLinkProps = LinkProps & {
  isActive: (pathname: string) => boolean;
};
export const desktopLinks = {
  left: [
    {
      href: '/portfolio',
      children: 'Portfolio',
      isActive: (pathname) => pathname.startsWith('/portfolio'),
    },
    {
      href: '/balances',
      children: 'Balances',
      isActive: (pathname) => pathname.startsWith('/balances'),
    },
    {
      href: '/market/btc',
      children: 'Market',
      isActive: (pathname) => pathname.startsWith('/market'),
    },
    {
      href: '/orders',
      children: 'Orders',
      isActive: (pathname) => pathname.startsWith('/orders'),
    },
    {
      href: '/boost/positions',
      children: 'Boost',
      isActive: (pathname) => pathname.startsWith('/boost'),
    },
    ...(isStablecoinStrategiesEnabled
      ? [
          {
            href: '/strategies',
            children: 'Strategies',
            isActive: (pathname: string) => pathname.startsWith('/strategies'),
          },
        ]
      : []),
  ],
  right: [
    {
      href: chainflipCommunityLinks.discord,
      children: <DiscordIcon />,
      target: '_blank',
      rel: 'noreferrer',
      isActive: () => false,
    },
    {
      href: chainflipCommunityLinks.telegram,
      children: <TelegramIcon />,
      target: '_blank',
      rel: 'noreferrer',
      isActive: () => false,
    },
    {
      href: chainflipCommunityLinks.twitter,
      children: <TwitterIcon />,
      target: '_blank',
      rel: 'noreferrer',
      isActive: () => false,
    },
  ],
} as const satisfies { left: ExtendedLinkProps[]; right: ExtendedLinkProps[] };

const LinkWithTransition = ({
  currentPathName,
  linkProps: { href, isActive, ...restProps },
}: {
  linkProps: ExtendedLinkProps;
  currentPathName: string;
}) => (
  <Link
    {...restProps}
    href={href}
    key={href}
    color="white"
    underline={isActive(currentPathName)}
    className={classNames(
      'font-aeonikMedium text-14 transition-colors duration-150 ease-out',
      isActive(currentPathName) && '!text-cf-green-1',
    )}
  />
);

const DesktopLinks = ({ currentPathName }: { currentPathName: string }) => (
  <div className="flex grow items-center justify-between ">
    <div className="flex gap-x-12">
      {desktopLinks.left.map((link) => (
        <LinkWithTransition currentPathName={currentPathName} linkProps={link} key={link.href} />
      ))}
    </div>
    <div className="flex gap-x-6">
      {desktopLinks.right.map((link) => (
        <LinkWithTransition currentPathName={currentPathName} linkProps={link} key={link.href} />
      ))}
    </div>
  </div>
);

const Navbar = ({
  mobileContent,
  logoHref,
  className,
  isMobileView = false,
}: {
  mobileContent?: ReactNode;
  logoHref: string;
  className?: string;
  isMobileView?: boolean;
}): JSX.Element => {
  const { pathname } = useRouter();
  const [openNavModal, setOpenNavModal] = useState(false);
  const { selectAccount, selectedAccount } = usePolkadot();
  const address = selectedAccount?.idSs58;

  useEffect(() => {
    if (openNavModal) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [openNavModal]);

  return (
    <div
      className={classNames(
        'z-[1] flex h-[74px] w-full flex-col items-center justify-center py-4',
        className,
      )}
    >
      {!openNavModal && (
        <div className="flex w-full items-center justify-between px-4 md:px-8">
          <div className="flex items-center">
            <Link href={logoHref}>
              <ChainflipLogo />
            </Link>
          </div>
          {isMobileView ? (
            address && (
              <div className="flex items-center gap-x-2">
                <Button
                  type="secondary-standard"
                  className="pointer-events-none flex items-center gap-x-2 whitespace-nowrap bg-transparent"
                >
                  {abbreviate(address)}
                  <ConnectAvatar address={address} size={16} />
                </Button>
                <Button
                  type="secondary-standard"
                  onClick={() => selectAccount(null)}
                  className="flex !min-w-[40px] items-center bg-transparent !p-0"
                >
                  <PowerIcon />
                </Button>
              </div>
            )
          ) : (
            <>
              <div className="hidden grow items-center justify-end gap-x-12 pl-8 pr-4 text-14 lg:flex">
                <DesktopLinks currentPathName={pathname} />
              </div>
              <div className="hidden gap-x-2 lg:flex">
                <PolkaConnectInfo />
                <MiscNavDropdownLinks />
              </div>
              <div onClick={() => setOpenNavModal(true)} className="cursor-pointer lg:hidden">
                <HamburgerIcon />
              </div>
            </>
          )}
        </div>
      )}
      {openNavModal && (
        <>
          <div className="flex w-full items-center justify-between px-4">
            <div className="flex items-center gap-x-[25px]">
              <Link href={logoHref}>
                <ChainflipLogo />
              </Link>
            </div>
            <button type="button" className="ml-auto" onClick={() => setOpenNavModal(false)}>
              <CloseIcon className="cursor-pointer" />
            </button>
          </div>
          <div className="fixed top-[74px] z-20 flex h-[calc(100vh-74px)] w-full flex-col overflow-y-scroll bg-black px-4">
            <div className="flex flex-col [&>*]:border-b [&>*]:border-b-cf-gray-3 [&>*]:py-5">
              {mobileContent}
              <ChainflipNetworkBadge titleSizeClass="text-16" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Navbar;
